import React, { Component } from 'react';
import { connect } from 'react-redux';
import { translate, showNotification } from 'react-admin';
import withStyles from '@material-ui/core/styles/withStyles';
import compose from 'recompose/compose';
import qs from 'qs';
import { withRouter } from 'react-router';
import Drawer from '@material-ui/core/Drawer';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import resourceConfig from './config';
import { paramToDictionary } from '../../../../utils';
import request from '../../../../network/request';

const MAX_FILES = 100;
const RETRY_COUNT = 3;
const RETRY_DELAY_MS = 1000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withRetry = async (fn, retries = RETRY_COUNT, delayMs = RETRY_DELAY_MS) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const result = await fn();
      if (result && result.ok) return result;
      if (attempt < retries) await delay(delayMs);
    } catch (e) {
      if (attempt < retries) await delay(delayMs);
      else throw e;
    }
  }
  return null;
};

const styles = {
  container: {
    width: 600,
    maxWidth: '90vw',
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
  },
  title: {
    marginBottom: 24,
  },
  section: {
    marginBottom: 16,
  },
  fileListContainer: {
    maxHeight: 360,
    overflowY: 'auto',
    border: '1px solid rgba(0,0,0,0.12)',
    borderRadius: 4,
    marginBottom: 16,
  },
  fileCountInfo: {
    marginTop: 8,
    fontSize: '0.75rem',
    color: 'rgba(0,0,0,0.54)',
  },
  listItemContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  listItemTextWrap: {
    flex: 1,
    overflow: 'hidden',
    marginRight: 8,
  },
  chipPending: {
    backgroundColor: '#e0e0e0',
    flexShrink: 0,
  },
  chipUploading: {
    backgroundColor: '#2196f3',
    color: 'white',
    flexShrink: 0,
  },
  chipSuccess: {
    backgroundColor: '#4caf50',
    color: 'white',
    flexShrink: 0,
  },
  chipError: {
    backgroundColor: '#f44336',
    color: 'white',
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 16,
  },
  cancelBtn: {
    marginLeft: 8,
  },
  loader: {
    marginLeft: 16,
    display: 'flex',
    alignItems: 'center',
  },
};

class AppModule extends Component {
  defaultParams = {};

  constructor(props) {
    super(props);
    this.state = {
      show: false,
      executing: false,
      sendToGisMt: false,
      selectedFiles: [],
      fileStatuses: [],
      params: { ...this.defaultParams },
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount = () => {
    this.checkLocation();
  };

  componentDidUpdate = (prevProps) => {
    if (this.props.location.search !== prevProps.location.search) {
      this.checkLocation();
    }
  };

  checkLocation = () => {
    if (this.props.location.search.length < 2) {
      this.resetState();
      return;
    }
    const params = qs.parse(this.props.location.search.slice(1));
    if (!(resourceConfig.urlParamName in params)) {
      this.resetState();
      return;
    }
    if (this.state.show) {
      return;
    }
    const data = paramToDictionary(params[resourceConfig.urlParamName]);
    this.setState({
      show: true,
      params: { ...this.defaultParams, ...data },
    });
  };

  resetState = () => {
    this.setState({ show: false, executing: false });
    setTimeout(this.afterClose, 500);
  };

  afterClose = () => {
    this.setState({
      params: { ...this.defaultParams },
      selectedFiles: [],
      fileStatuses: [],
      sendToGisMt: false,
    });
  };

  handleFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, MAX_FILES);
    this.setState({
      selectedFiles: files,
      fileStatuses: files.map((f) => ({
        name: f.name,
        status: 'pending',
        docId: null,
        error: null,
      })),
    });
    e.target.value = '';
  };

  updateFileStatus = (index, status, docId = null, error = null) => {
    this.setState((prevState) => {
      const fileStatuses = [...prevState.fileStatuses];
      fileStatuses[index] = { ...fileStatuses[index], status, docId, error };
      return { fileStatuses };
    });
  };

  handleSendToGisMtChange = (e) => {
    this.setState({ sendToGisMt: e.target.checked });
  };

  execute = async () => {
    const { selectedFiles, sendToGisMt, params } = this.state;
    const { showNotification } = this.props;

    if (!selectedFiles.length) return;

    this.setState({ executing: true });

    let successCount = 0;
    const docType = params.docType || 'ATK';

    for (let i = 0; i < selectedFiles.length; i++) {
      this.updateFileStatus(i, 'uploading');

      const fetchBody = new FormData();
      fetchBody.append('file', selectedFiles[i]);
      fetchBody.append('docType', docType);

      let response = null;
      try {
        response = await withRetry(() =>
          request({
            apiService: resourceConfig.apiService,
            path: resourceConfig.apiPath,
            body: fetchBody,
            method: 'POST',
          }),
        );
      } catch (e) {
        console.error(e);
      }

      if (response && response.ok && response.data != null) {
        const docId = response.data;
        this.updateFileStatus(i, 'success', docId);
        successCount++;

        if (sendToGisMt) {
          try {
            await withRetry(() =>
              request({
                apiService: resourceConfig.apiService,
                path: resourceConfig.sendPath,
                urlParams: { id: docId },
                method: 'POST',
              }),
            );
          } catch (e) {
            console.error('Send to GIS MT failed for doc', docId, e);
          }
        }
      } else {
        const errMsg =
          (response && response.data && (response.data.exMessage || response.data.message)) || '';
        this.updateFileStatus(i, 'error', null, errMsg);
      }
    }

    const totalCount = selectedFiles.length;

    if (successCount === 0) {
      showNotification(`resources.${resourceConfig.name}.errorMessage`, 'error');
    } else if (successCount < totalCount) {
      showNotification(`resources.${resourceConfig.name}.partialSuccessMessage`, 'warning');
    } else {
      showNotification(`resources.${resourceConfig.name}.successMessage`, 'success');
    }

    this.setState({ executing: false });
  };

  handleCloseClick = () => {
    this.props.history.goBack();
  };

  getChipClass = (status) => {
    const { classes } = this.props;
    const map = {
      pending: classes.chipPending,
      uploading: classes.chipUploading,
      success: classes.chipSuccess,
      error: classes.chipError,
    };
    return map[status] || classes.chipPending;
  };

  getStatusLabel = (status) => {
    const { translate } = this.props;
    return translate(`resources.${resourceConfig.name}.status.${status}`);
  };

  render() {
    const { translate, classes } = this.props;
    const { show, executing, sendToGisMt, selectedFiles, fileStatuses } = this.state;

    return (
      <Drawer anchor="right" open={show} onClose={this.handleCloseClick}>
        <div className={classes.container}>
          <Typography variant="h6" className={classes.title}>
            {translate(`resources.${resourceConfig.name}.title`)}
          </Typography>

          <div className={classes.section}>
            <input
              type="file"
              multiple
              accept=".xls,.xlsx"
              onChange={this.handleFileChange}
              ref={this.fileInputRef}
              style={{ display: 'none' }}
              id="atk-file-input"
            />
            <label htmlFor="atk-file-input">
              <Button variant="outlined" component="span" disabled={executing}>
                {translate(`resources.${resourceConfig.name}.selectFiles`)}
              </Button>
            </label>
            {selectedFiles.length > 0 && (
              <Typography variant="body2" className={classes.fileCountInfo}>
                {translate(`resources.${resourceConfig.name}.filesSelected`, {
                  count: selectedFiles.length,
                  max: MAX_FILES,
                })}
              </Typography>
            )}
          </div>

          {fileStatuses.length > 0 && (
            <div className={classes.fileListContainer}>
              <List dense disablePadding>
                {fileStatuses.map((fs, idx) => (
                  <React.Fragment key={idx}>
                    <ListItem>
                      <div className={classes.listItemContent}>
                        <div className={classes.listItemTextWrap}>
                          <ListItemText
                            primary={fs.name}
                            secondary={
                              fs.error
                                ? fs.error
                                : fs.docId != null
                                ? `ID: ${fs.docId}`
                                : null
                            }
                            primaryTypographyProps={{ noWrap: true }}
                          />
                        </div>
                        <Chip
                          label={this.getStatusLabel(fs.status)}
                          className={this.getChipClass(fs.status)}
                          size="small"
                        />
                      </div>
                    </ListItem>
                    {idx < fileStatuses.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </div>
          )}

          <div className={classes.section}>
            <FormControlLabel
              control={
                <Switch
                  checked={sendToGisMt}
                  onChange={this.handleSendToGisMtChange}
                  color="primary"
                  disabled={executing}
                />
              }
              label={translate(`resources.${resourceConfig.name}.sendToGisMt`)}
            />
          </div>

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={this.execute}
              disabled={executing || selectedFiles.length === 0}
            >
              {translate(`resources.${resourceConfig.name}.executeAction`)}
            </Button>
            {executing && (
              <div className={classes.loader}>
                <CircularProgress size={24} />
              </div>
            )}
            <Button
              onClick={this.handleCloseClick}
              disabled={executing}
              className={classes.cancelBtn}
            >
              {translate(`resources.${resourceConfig.name}.cancelAction`)}
            </Button>
          </div>
        </div>
      </Drawer>
    );
  }
}

export default compose(
  withRouter,
  connect(null, { showNotification }),
  translate,
  withStyles(styles),
)(AppModule);
